
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';
import { isNativeApp } from '@/lib/capacitor';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export function PWAInstallPrompt() {
  const _isNative = isNativeApp();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Don't show this prompt on mobile, use MobilePWAPrompt instead
    if (isMobile) {
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if permanently dismissed
    const permanentlyDismissed = localStorage.getItem('pwa-permanently-dismissed');
    if (permanentlyDismissed) {
      return;
    }

    // Listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    // Check if temporarily dismissed (show again after 3 days instead of 7)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      
      if (dismissedDate > threeDaysAgo) {
        setShowInstallPrompt(false);
        return;
      }
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isMobile]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    try {
      deferredPrompt.prompt();
      
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem('pwa-installed', 'true');
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    } catch (error) {
      logger.error('Error during installation:', toError(error).message);
    }
  };

  const handleTemporaryDismiss = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  const handlePermanentDismiss = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-permanently-dismissed', new Date().toISOString());
  };

  // Only show on desktop
  if (_isNative || isInstalled || !showInstallPrompt || !deferredPrompt || isMobile) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 right-4 w-80 z-50 shadow-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            Installer Sclepios IA
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleTemporaryDismiss}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription className="text-xs">
          Accédez à Sclepios même hors-ligne ! Parfait pour les hôpitaux avec une connexion limitée.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <Button
          onClick={handleInstallClick}
          className="w-full text-sm"
          size="sm"
        >
          <Download className="mr-2 h-4 w-4" />
          Installer l'application
        </Button>
        <Button
          variant="ghost"
          onClick={handlePermanentDismiss}
          className="w-full text-xs text-muted-foreground"
          size="sm"
        >
          Ne plus proposer
        </Button>
      </CardContent>
    </Card>
  );
}
